import _ from 'underscore';
import getConfig from 'next/config';
import Joi from 'joi';

import { addCleanCache, parsePathParams } from '@culture/common-helpers/params';
import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addQuery, filterQuery } from '@culture/common-helpers/url';

import { publicationTypesHash } from '@culture/utils/helpers/publications';
import { statusesHash } from '@culture/utils/helpers/statuses';
import Template from 'pageTemplates/news';

const { serverRuntimeConfig } = getConfig();

const BASE_RUBRIC_PATH = 'news';

const getNews = ({ urlParams, filtersQueryParams, rubricId }) => {
	const params = filterQuery({
		..._(filtersQueryParams).pick('query', 'page'),
		fields: [
			'title',
			'name',
			'thumbnailFile',
			'type',
			'podcasts',
			'publishDate',
			'showDefaultThumbnail'
		].join(),
		cached: true,
		statuses: [statusesHash.published],
		types: [publicationTypesHash.news],
		rubrics: [rubricId],
		sort: '-publishDate',
		limit: 16
	});

	return apiRequest(
		'publications',
		addCleanCache(params, urlParams.cleanCache)
	);
};

const getRelatedPublications = (cleanCache) => {
	const params = filterQuery({
		fields: [
			'title',
			'name',
			'thumbnailFile',
			'publishDate',
			'type',
			'shortText'
		].join(),
		cached: true,
		statuses: ['published'],
		types: [publicationTypesHash.articles, publicationTypesHash.themes],
		limit: 5
	});

	return apiRequest('publications', addCleanCache(params, cleanCache));
};

const getRubrics = ({ rubricPath, cleanCache }) =>
	apiRequest(
		'rubrics',
		addCleanCache(
			{
				paths: [
					!rubricPath
						? BASE_RUBRIC_PATH
						: [BASE_RUBRIC_PATH, rubricPath].join('/')
				]
			},
			cleanCache
		)
	);

const getMeta = (page, rubricPath, cleanCache) => {
	const params = addCleanCache(
		{
			rubricPath: !rubricPath
				? BASE_RUBRIC_PATH
				: [BASE_RUBRIC_PATH, rubricPath].join('/'),
			page,
			cached: true
		},
		cleanCache
	);

	return apiRequest('meta/catalog', params);
};
const getBroadcasts = (cleanCache) => {
	const params = addCleanCache(
		{
			limit: 1,
			statuses: [statusesHash.published],
			fields: [
				'title',
				'name',
				'thumbnailFile',
				'fromDate',
				'type',
				'toDate',
				'publishDate',
				'institute.title'
			].join(','),
			sort: 'fromDate',
			fromDateStart: new Date().toISOString(),
			cached: true,
			isWasBroadcast: true
		},
		cleanCache
	);

	return apiRequest('broadcast', params);
};

const VALID_RUBRIC_PATHS = [
	'cinema',
	'museums',
	'music',
	'theaters',
	'traditions',
	'literature',
	'news'
];

const catalogDescription =
	'Музыкальные фестивали и литературные премии, театральные программы и новинки кино, подборки интересных событий в регионах и лучшие выставки столичных музеев — в разделе портала «Новости культуры»';

export async function getServerSideProps({ query, resolvedUrl }) {
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean().failover(false),
		limit: Joi.number().default(24).min(1),
		query: Joi.string(),
		page: Joi.number().default(1).min(1).failover(1),
		pathParams: Joi.array().items(Joi.string()),
		rubric: Joi.string()
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const { rubric: parsedRubric } = parsePathParams(params.pathParams);

	const rubricPath = !_.isEmpty(parsedRubric) ? parsedRubric[0] : undefined;

	const { cleanCache, page } = params;

	const rubrics = await getRubrics({
		rubricPath,
		cleanCache
	});
	const rubric = !_.isEmpty(rubrics?.items) ? rubrics.items[0] : null;

	if (rubricPath && !VALID_RUBRIC_PATHS.includes(rubricPath) && !rubric) {
		return {
			redirect: {
				destination: '/news',
				permanent: false
			}
		};
	}

	const [news, meta, broadcasts] = await batchApiRequest([
		getNews({
			filtersQueryParams: params,
			urlParams: params,
			rubricId: rubric?._id
		}),
		getMeta(page, rubricPath, cleanCache),
		getBroadcasts(cleanCache)
	]);
	const publications = await getRelatedPublications(cleanCache);

	if (news.pagination.total && news.pagination.total < params.page) {
		return {
			redirect: {
				destination: addQuery(`${serverRuntimeConfig.baseUrl}${resolvedUrl}`, {
					page: news.pagination.total
				}),
				permanent: false
			}
		};
	}

	const broadcast = !_.isEmpty(broadcasts?.items) ? broadcasts.items[0] : null;
	const relatedEntities = { broadcast, publications: publications.items };

	const urlParams = {
		..._(params).omit('limit', 'pathParams'),
		rubric: rubricPath || null
	};

	return {
		props: {
			news,
			meta,
			urlParams,
			catalogDescription,
			relatedEntities,
			template: 'catalog'
		}
	};
}

export default Template;
